/**
 * V1.2 Block 5 — "Für mich freigegeben": everything the logged-in user holds
 * an acl_entry on — folders AND files, across workspaces (a single shared file
 * must not vanish for the recipient; precision to D-07).
 *
 * 🔴 Pure listing. The guard is untouched; nothing is preloaded (access
 * cascades on open, loading never — review addition 2026-07-23). A shared
 * folder expands INLINE through the normal guarded listing endpoint; boards
 * open through the board route, which resolves the owning workspace itself.
 */

import { Fragment, useEffect, useState } from "react";

import { ApiError, ryfeApi } from "../api";
import { serializeScene } from "../board-data";
import { createUrlBookmark, uploadFileToFolder } from "../file-actions";
import { navigate } from "../router";
import { routeForKind } from "../open-file";
import { useRyfe } from "../RyfeContext";

import { AddFileDialog, type NewEntryKind } from "./AddFileDialog";

import "./ryfe.scss";

import type { FolderListing, SharedItem, TemplateFile } from "../api";

const canWrite = (role: string) => role === "editor" || role === "owner";

const ROLE_LABELS: Record<string, string> = {
  owner: "Mitbesitzer",
  editor: "Bearbeiten",
  viewer: "Lesen",
};

export const RyfeSharedWithMe = ({
  embedded = false,
}: {
  // Welle A (Datei-Master) — when embedded as the second table on „Meine
  // Dateien", drop the standalone page wrapper/title for a section heading.
  embedded?: boolean;
} = {}) => {
  // P12 — „Aus Vorlage" in einem geteilten (fremden) Ordner nutzt die EIGENEN
  // Vorlagen des aktiven Workspace (Nutzerwunsch: „jeder sieht seine eigenen
  // Vorlagen"), per Cross-Workspace-Fork in den fremden Ordner.
  const { workspace } = useRyfe();
  const myWsId = workspace?.id ?? null;
  const [items, setItems] = useState<SharedItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Inline expansion of one shared folder at a time (guarded listing).
  // Revision R3: `path` erlaubt das Absteigen in Unterordner (vorher waren sie
  // tote Zeilen); path[0] ist der freigegebene Wurzel-Ordner, das letzte
  // Element der aktuell gelistete Ordner.
  const [open, setOpen] = useState<{
    id: string;
    wsId: string;
    role: string;
    path: { id: string; name: string }[];
    listing: FolderListing | null;
  } | null>(null);
  // CC_07 Block 4/5 — „Datei hinzufügen" inside an expanded shared folder.
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  // P12 — die EIGENEN Vorlagen (aktiver Workspace), nutzbar auch im geteilten Ordner.
  const [ownTemplates, setOwnTemplates] = useState<TemplateFile[]>([]);
  // Mobile row menu (⋮): rename / move-to-trash for shared items the caller may
  // write. Right-click has no touch equivalent, so this is the only entry here.
  const [menu, setMenu] = useState<{
    x: number;
    y: number;
    it: SharedItem;
  } | null>(null);

  useEffect(() => {
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  // P12 — load the caller's OWN templates (active workspace) while the add
  // dialog is open; these are usable in any folder the caller may write.
  useEffect(() => {
    if (!adding || !myWsId) {
      setOwnTemplates([]);
      return;
    }
    let cancelled = false;
    ryfeApi
      .listTemplates(myWsId)
      .then((t) => {
        if (!cancelled) {
          setOwnTemplates(t.templates);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOwnTemplates([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adding, myWsId]);

  // P12 — fork one of the caller's OWN templates (source ws = myWsId) into the
  // shared foreign folder (dest ws = open.wsId) and open it (from=shared → the
  // editor's back button returns to „Für mich freigegeben", not a 404).
  const createFromTemplateShared = async (templateId: string, name: string) => {
    if (!open || !myWsId) {
      return;
    }
    const folderId = currentFolderId(open);
    setBusy(true);
    try {
      const created = await ryfeApi.forkFile(open.wsId, templateId, {
        folderId,
        name,
        sourceWorkspaceId: myWsId,
      });
      setAdding(false);
      const route = routeForKind(created.kind, created.id);
      if (route) {
        navigate(`${route}?from=shared`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Aus Vorlage fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const openKebab = (e: React.MouseEvent, it: SharedItem) => {
    e.preventDefault();
    e.stopPropagation();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const left = Math.max(8, Math.min(r.left, window.innerWidth - 180 - 8));
    setMenu({ x: left, y: r.bottom + 2, it });
  };

  const reload = async () => {
    try {
      const r = await ryfeApi.sharedWithMe();
      setItems(r.items);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Laden fehlgeschlagen");
    }
  };

  const renameItem = async (it: SharedItem) => {
    const name = window.prompt("Neuer Name", it.name);
    if (!name || name === it.name) {
      return;
    }
    try {
      if (it.kind === "file") {
        await ryfeApi.patchFile(it.workspaceId, it.id, { name });
      } else {
        await ryfeApi.patchFolder(it.workspaceId, it.id, { name });
      }
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Umbenennen fehlgeschlagen");
    }
  };

  const removeItem = async (it: SharedItem) => {
    if (!window.confirm(`„${it.name}“ in den Papierkorb verschieben?`)) {
      return;
    }
    try {
      if (it.kind === "file") {
        await ryfeApi.deleteFile(it.workspaceId, it.id);
      } else {
        await ryfeApi.deleteFolder(it.workspaceId, it.id);
      }
      if (open?.id === it.id) {
        setOpen(null);
      }
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Löschen fehlgeschlagen");
    }
  };

  // Der aktuell gelistete Ordner (Drill-down-Ziel) — nicht zwingend die Wurzel.
  const currentFolderId = (o: NonNullable<typeof open>) =>
    o.path.length > 0 ? o.path[o.path.length - 1].id : o.id;

  const createInShared = async (kind: NewEntryKind, name: string) => {
    if (!open) {
      return;
    }
    const folderId = currentFolderId(open);
    setBusy(true);
    try {
      if (kind === "folder") {
        await ryfeApi.createFolder(open.wsId, folderId, name);
      } else if (kind === "md") {
        const f = await ryfeApi.createFile(
          open.wsId,
          folderId,
          name,
          "md",
          `# ${name}\n\n`,
          "text/markdown",
        );
        navigate(`/md/${f.id}?from=shared`);
        return;
      } else if (kind === "list") {
        const f = await ryfeApi.createFile(
          open.wsId,
          folderId,
          name,
          "list",
          "",
        );
        navigate(`/list/${f.id}?from=shared`);
        return;
      } else {
        const f = await ryfeApi.createFile(
          open.wsId,
          folderId,
          name,
          "board",
          serializeScene([], {}, {}),
        );
        navigate(`/board/${f.id}?from=shared`);
        return;
      }
      // Folder created → refresh the inline listing in place.
      setAdding(false);
      const l = await ryfeApi.listFolder(open.wsId, folderId);
      setOpen((o) => (o ? { ...o, listing: l } : o));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Anlegen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  // Welle B (Punkt 10) — URL-Lesezeichen im aktuell geöffneten geteilten Ordner.
  const createUrl = async (name: string, url: string) => {
    if (!open) {
      return;
    }
    const folderId = currentFolderId(open);
    setBusy(true);
    try {
      await createUrlBookmark(open.wsId, folderId, name, url);
      setAdding(false);
      const l = await ryfeApi.listFolder(open.wsId, folderId);
      setOpen((o) => (o ? { ...o, listing: l } : o));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Anlegen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  // Welle B (Punkt 10) — Datei hochladen (Format→Typ via upload-map).
  const upload = async (file: File) => {
    if (!open) {
      return;
    }
    const folderId = currentFolderId(open);
    setBusy(true);
    try {
      const created = await uploadFileToFolder(open.wsId, folderId, file);
      setAdding(false);
      const route = routeForKind(created.kind, created.id);
      if (route) {
        navigate(`${route}?from=shared`);
      } else {
        const l = await ryfeApi.listFolder(open.wsId, folderId);
        setOpen((o) => (o ? { ...o, listing: l } : o));
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    ryfeApi
      .sharedWithMe()
      .then((r) => setItems(r.items))
      .catch((e) =>
        setError(e instanceof ApiError ? e.message : "Laden fehlgeschlagen"),
      );
  }, []);

  const toggleFolder = async (item: SharedItem) => {
    if (open?.id === item.id) {
      setOpen(null);
      return;
    }
    const base = {
      id: item.id,
      wsId: item.workspaceId,
      role: item.role,
      path: [{ id: item.id, name: item.name }],
    };
    setOpen({ ...base, listing: null });
    try {
      const l = await ryfeApi.listFolder(item.workspaceId, item.id);
      setOpen({ ...base, listing: l });
    } catch {
      setOpen(null);
      setError("Ordner kann nicht geöffnet werden (Zugriff entzogen?).");
    }
  };

  // Revision R3 — in einen Unterordner absteigen bzw. im Pfad zurückspringen.
  // Die Listung läuft über denselben guard-gegateten Endpoint wie die Wurzel.
  const drillTo = async (
    folderId: string,
    name: string,
    upToIndex?: number,
  ) => {
    if (!open) {
      return;
    }
    const path =
      upToIndex !== undefined
        ? open.path.slice(0, upToIndex + 1)
        : [...open.path, { id: folderId, name }];
    setOpen({ ...open, path, listing: null });
    try {
      const l = await ryfeApi.listFolder(open.wsId, folderId);
      setOpen((o) => (o ? { ...o, path, listing: l } : o));
    } catch {
      setOpen((o) => (o ? { ...o, listing: null } : o));
      setError("Ordner kann nicht geöffnet werden (Zugriff entzogen?).");
    }
  };

  return (
    <div className={embedded ? "ryfe-section" : "ryfe-page"}>
      {embedded ? (
        <h2 className="ryfe-section__title">Für mich freigegeben</h2>
      ) : (
        <h1 className="ryfe-page__title">Für mich freigegeben</h1>
      )}

      {error && <div className="ryfe-auth__error">{error}</div>}

      {!items ? (
        <div className="ryfe-muted">Laden…</div>
      ) : items.length === 0 ? (
        <div className="ryfe-muted">
          Noch nichts — sobald jemand einen Ordner oder eine Datei für dich
          freigibt, erscheint sie hier.
        </div>
      ) : (
        <div className="ryfe-table-wrap">
          <table className="ryfe-table">
            <thead>
              <tr>
                <th className="ryfe-th--kebab" aria-label="Aktionen" />
                <th>Name</th>
                <th>Typ</th>
                <th>Workspace</th>
                <th>Meine Rolle</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <Fragment key={`${it.kind}-${it.id}`}>
                  <tr className="ryfe-row">
                    <td className="ryfe-td--kebab">
                      <button
                        className="ryfe-kebab"
                        aria-label={`Aktionen für ${it.name}`}
                        title="Aktionen"
                        onClick={(e) => openKebab(e, it)}
                      >
                        ⋮
                      </button>
                    </td>
                    <td>
                      {it.kind === "folder" ? (
                        <button
                          className="ryfe-linkish"
                          onClick={() => toggleFolder(it)}
                        >
                          📁 {it.name}
                        </button>
                      ) : routeForKind(it.fileKind, it.id) ? (
                        // Revision R3 — Route-nach-kind statt board-only: auch
                        // geteilte Notizen/Listen öffnen (die Editoren lösen
                        // den fremden Workspace selbst auf).
                        <button
                          className="ryfe-linkish"
                          onClick={() => {
                            const route = routeForKind(it.fileKind, it.id);
                            if (route) {
                              navigate(`${route}?from=shared`);
                            }
                          }}
                        >
                          {it.fileKind === "board" ? "🗒" : "📄"} {it.name}
                        </button>
                      ) : (
                        <span title="Für diesen Dateityp gibt es keinen Editor">
                          📄 {it.name}
                        </span>
                      )}
                    </td>
                    <td>{it.kind === "folder" ? "Ordner" : it.fileKind}</td>
                    <td>{it.workspaceName}</td>
                    <td>{ROLE_LABELS[it.role] || it.role}</td>
                  </tr>
                  {open?.id === it.id && (
                    <tr key={`open-${it.id}`}>
                      <td colSpan={5}>
                        {!open.listing ? (
                          <div className="ryfe-muted">Laden…</div>
                        ) : (
                          <div className="ryfe-sharedtree">
                            {/* Revision R3 — klickbarer Pfad im freigegebenen
                                Baum (Absteigen + Zurückspringen). */}
                            {open.path.length > 1 && (
                              <nav
                                className="ryfe-breadcrumbs"
                                aria-label="Pfad"
                              >
                                {open.path.map((p, idx) =>
                                  idx < open.path.length - 1 ? (
                                    <span key={p.id}>
                                      <button
                                        className="ryfe-linkish"
                                        onClick={() =>
                                          void drillTo(p.id, p.name, idx)
                                        }
                                      >
                                        {p.name}
                                      </button>
                                      {" / "}
                                    </span>
                                  ) : (
                                    <span key={p.id}>{p.name}</span>
                                  ),
                                )}
                              </nav>
                            )}
                            {canWrite(open.role) && (
                              <button
                                className="ryfe-btn ryfe-btn--ghost"
                                onClick={() => setAdding(true)}
                              >
                                + Datei hinzufügen
                              </button>
                            )}
                            {open.listing.folders.map((f) => (
                              <button
                                key={f.id}
                                className="ryfe-linkish"
                                onClick={() => void drillTo(f.id, f.name)}
                              >
                                📁 {f.name}
                              </button>
                            ))}
                            {open.listing.files.map((f) => {
                              const route = routeForKind(f.kind, f.id);
                              return route ? (
                                <button
                                  key={f.id}
                                  className="ryfe-linkish"
                                  onClick={() =>
                                    navigate(`${route}?from=shared`)
                                  }
                                >
                                  {f.kind === "board" ? "🗒" : "📄"} {f.name}
                                </button>
                              ) : (
                                <div
                                  key={f.id}
                                  className="ryfe-muted"
                                  title="Für diesen Dateityp gibt es keinen Editor"
                                >
                                  📄 {f.name}
                                </div>
                              );
                            })}
                            {open.listing.folders.length === 0 &&
                              open.listing.files.length === 0 && (
                                <div className="ryfe-muted">leer</div>
                              )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {menu && (
        <ul
          className="ryfe-context"
          style={{ top: menu.y, left: menu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <li>
            <button
              disabled={!canWrite(menu.it.role)}
              onClick={() => {
                renameItem(menu.it);
                setMenu(null);
              }}
            >
              Umbenennen
            </button>
          </li>
          <li>
            <button
              className="ryfe-context__danger"
              disabled={
                menu.it.kind === "folder"
                  ? menu.it.role !== "owner"
                  : !canWrite(menu.it.role)
              }
              onClick={() => {
                removeItem(menu.it);
                setMenu(null);
              }}
            >
              Löschen
            </button>
          </li>
        </ul>
      )}

      {adding && (
        <AddFileDialog
          busy={busy}
          onClose={() => setAdding(false)}
          onCreate={createInShared}
          onCreateUrl={createUrl}
          onUpload={upload}
          templates={ownTemplates}
          onCreateFromTemplate={createFromTemplateShared}
        />
      )}
    </div>
  );
};
